/** Пошаговая инструкция для всех ролей. На русском, простым языком. */
export default function Help() {
  return (
    <div className="container help">
      <h1>Как пользоваться TEKSHER</h1>
      <p className="help-lead">
        Эта система отслеживает коды маркировки TechShare: какие пришли, какие уже уехали, какие
        можно положить в новую отгрузку. Ниже — пошаговая инструкция для четырёх ролей и сквозной
        путь отгрузки от сканирования до отправки на маркетплейс.
      </p>

      <div className="card">
        <h2>Кто что делает</h2>
        <ul>
          <li>
            <b>Администратор</b> (<span className="mono">admin</span>) — всё умеет: загружает
            партии, заводит пользователей (в том числе менеджеров маркетплейса), отменяет
            ошибки, закрывает отгрузки, проставляет поставку, если нужно. Может{" "}
            <b>удалить отгрузку</b> в любом статусе до{" "}
            <span className="status status-closed">Отгружена</span> (кнопка «Удалить» на карточке).
            При удалении все привязанные коды возвращаются в{" "}
            <span className="status status-free">free</span> и могут быть использованы снова.
          </li>
          <li>
            <b>Сканировка</b> (<span className="mono">scan_operator</span>) — создаёт отгрузки и
            сканирует коды до «Собрана». Может <b>удалить отгрузку</b>, пока она в статусе
            «Черновик» или «Сканируется» — например, пустую, созданную по ошибке или
            тестовую. Коды из неё вернутся в <span className="status status-free">free</span>.
            Собранную отгрузку удаляет только администратор.
          </li>
          <li>
            <b>Контролёр / склад</b> (<span className="mono">controller</span>) — видит остатки,
            списывает испорченные коды.
          </li>
          <li>
            <b>Оператор</b> (<span className="mono">operator</span>) — загружает партии из
            TechShare, создаёт отгрузки, сканирует коды, скачивает CSV для загрузки обратно,
            закрывает отгрузку после подтверждения TechShare.
          </li>
          <li>
            <b>Менеджер маркетплейса</b> (<span className="mono">marketplace_manager</span>) —
            работает уже с закрытыми отгрузками: бронирует слот приёмки на маркетплейсе (WB,
            Ozon, Яндекс.Маркет и т.д.), вводит номер поставки в TEKSHER, после чего в
            рабочий чат Bitrix24 автоматически уходит уведомление склада и оператора.
          </li>
        </ul>
        <p className="muted">
          Каждый шаг ниже подписан, кому он нужен. Если шаг не для вашей роли — просто
          пропустите его; кнопок, которые вам не положены, вы в интерфейсе не увидите.
        </p>
      </div>

      <div className="card">
        <h2>🔑 Шаг 1. Вход</h2>
        <ol>
          <li>
            Откройте{" "}
            <span className="mono">https://kg.ignore.pro</span> в браузере. Лучше всего{" "}
            <b>Google Chrome</b> или <b>Microsoft Edge</b> (сканер работает только в них).
          </li>
          <li>Введите логин и пароль, которые вам выдал администратор.</li>
          <li>Если логин или пароль не подходит — попросите админа создать или сбросить.</li>
        </ol>
      </div>

      <div className="card">
        <h2>📥 Шаг 2. Загрузка партии кодов (админ и оператор)</h2>
        <ol>
          <li>
            В TechShare закажите коды и скачайте CSV-файл. <b>Ничего в нём не меняйте</b> —
            не открывайте в Excel, не пересохраняйте, не переименовывайте содержимое. Файл должен
            остаться ровно таким, как его отдал TechShare.
          </li>
          <li>
            В TEKSHER откройте раздел <NavHint>Партии</NavHint>.
          </li>
          <li>
            Выберите <b>юрлицо</b> (кабинет Ozon), которому принадлежат эти коды — у каждого
            юрлица свои коды TechShare. Затем нажмите «Выберите файл», найдите тот самый CSV,
            нажмите «Загрузить».
          </li>
          <li>
            Под формой появится строчка «Импортировано: <b>N</b>, пропущено (дубли): <b>M</b>».
            <b>N</b> — сколько новых кодов добавилось на склад. <b>M</b> — сколько строк в файле
            уже были у нас. Если есть «ошибки парсинга» — пришлите снимок экрана админу системы.
          </li>
        </ol>
        <p className="muted">
          Сохранять исходный CSV у себя не нужно — TEKSHER хранит его побайтно и сам потом соберёт
          из него выгрузку обратно.
        </p>
        <div className="help-warn">
          <b>Допзаказ кодов (догрузка):</b> если заказали у TechShare дополнительные коды, а он
          в ответ прислал один файл с новыми и старыми строками вместе — просто загрузите этот
          файл как обычно. Старые коды пропустятся (попадут в «пропущено (дубли)»), новые
          добавятся в новую партию. Никакой ручной чистки файла от старых строк делать не надо,
          и из-за повторной загрузки ничего не дублируется.
        </div>
        <p>
          На той же странице <NavHint>Партии</NavHint> под списком партий есть
          два блока:
        </p>
        <ul>
          <li>
            <b>«Артикулы»</b> — сколько свободных кодов осталось по каждому
            товару Ozon-справочника. Группы схлопнуты по «артикул_цвет»
            (например <span className="mono">100700_черный</span>); размеры
            раскрываются по кнопке <span className="mono">+</span>. Сортировка —
            по убыванию остатка.
          </li>
          <li>
            <b>«GTIN без артикулов»</b> — штрихкоды, которых нет в
            Ozon-справочнике. Нужно залить обновлённый{" "}
            <span className="mono">ozn_tov.csv</span> в <NavHint>Ozon-справочник</NavHint>,
            и они исчезнут отсюда.
          </li>
        </ul>
      </div>

      <div className="card">
        <h2>📦 Шаг 3. Создать отгрузку</h2>
        <ol>
          <li>
            Раздел <NavHint>Отгрузки</NavHint> → форма «Новая отгрузка».
          </li>
          <li>
            Выберите <b>юрлицо</b> (кабинет Ozon) — обязательно. От него зависит, против
            какого справочника товаров пойдёт сличение и в какой кабинет готовится файл
            поставки. Например <i>ИП Осмонов — ТопДжинс</i> или <i>ООО Дигр Групп</i>.
          </li>
          <li>
            Заполните <b>номер</b> (обязательно — любой короткий идентификатор, например{" "}
            <span className="mono">SH-2026-04-20-01</span>). Название, склад, комментарий —
            по желанию.
          </li>
          <li>Нажмите «Создать». В списке ниже появится новая строка со статусом «draft».</li>
          <li>Кликните на её номер — откроется карточка отгрузки, в которую добавляются коды.</li>
        </ol>
      </div>

      <div className="card">
        <h2>🔌 Шаг 4. Подключить сканер</h2>
        <p>
          Сканер подключается прямо в браузере через USB или Bluetooth-serial. Делается один раз
          за смену (при обновлении страницы придётся повторить — это защита браузера).
        </p>
        <ol>
          <li>Включите сканер, подключите USB к компьютеру. Если у него есть переключатель режимов, выберите <b>Serial (COM)</b>, не «Клавиатура» / не «HID».</li>
          <li>
            Откройте карточку своей отгрузки. В разделе «Добавить код (сканирование)» нажмите{" "}
            <button className="primary" disabled>
              Подключить сканер
            </button>
            .
          </li>
          <li>
            Браузер покажет окно со списком COM-портов. Выберите свой сканер (часто называется{" "}
            <span className="mono">USB Serial Device</span> или с именем производителя — Honeywell,
            Zebra, Datalogic) и нажмите «Подключить».
          </li>
          <li>
            Индикатор слева от кнопки поменяет цвет: <ScannerBadge kind="connecting" /> → <ScannerBadge kind="connected" />. Теперь каждый физический скан
            попадёт прямо в отгрузку.
          </li>
        </ol>
        <div className="help-warn">
          <b>Если кнопка «Web Serial не поддерживается»</b> — значит открыт Firefox или Safari.
          В них сканер не подключится. Откройте страницу в Chrome или Edge.
        </div>
        <div className="help-warn">
          <b>Если сканера нет в списке портов</b> — проверьте, что он в режиме Serial/COM, а не
          «Клавиатура». У разных моделей это разные штрихкоды/кнопки в настройке — смотрите
          паспорт сканера.
        </div>
      </div>

      <div className="card">
        <h2>📦 Шаг 4.5. Это наборы / комплекты?</h2>
        <p>
          При первом заходе на отгрузку (статус{" "}
          <span className="status status-draft">Новая</span>) система спросит:{" "}
          <b>«Эта отгрузка содержит наборы?»</b>. Это влияет на то, как
          считаются количество в файле для Ozon и прогресс сборки.
        </p>
        <ul>
          <li>
            <b>Нет, одиночные товары</b> — каждый КИЗ продаётся отдельной
            единицей. Это привычный режим.
          </li>
          <li>
            <b>Да, наборы</b> — несколько КИЗ упакованы как один товарный
            набор (например, набор из 3 разных цветов одного артикула; для
            маркировки это один GTIN).
          </li>
        </ul>
        <p className="help-warn">
          Ответить нужно <b>до первого скана</b>: после первого сканирования
          поменять режим уже нельзя — у каждого кода фиксируется номер набора.
          Если случайно выбрали не тот режим — удалите все коды или удалите
          отгрузку целиком (кнопка «Удалить», пока она не собрана).
        </p>
        <h3 style={{ marginTop: 16 }}>Если выбрали «Да, наборы»</h3>
        <ol>
          <li>
            Отсканируйте <b>полностью первый набор</b> — все КИЗ, которые
            должны лежать в одной товарной упаковке.
          </li>
          <li>
            Нажмите большую синюю кнопку{" "}
            <button className="primary" disabled>
              ✓ Набор собран
            </button>
            — она появляется под формой сканирования в режиме обучения.
          </li>
          <li>
            Появится подтверждение: <b>«В наборе X шт — верно?»</b>. Если
            ошиблись — нажмите <b>Нет, пересдать</b>: все коды первого набора
            удалятся, и обучение начнётся заново.
          </li>
          <li>
            После «Да, продолжить» размер набора фиксируется на всю
            отгрузку. В шапке появится бейдж{" "}
            <span
              style={{
                background: "#fff7e6",
                border: "1px solid #ffd591",
                color: "#ad6800",
                borderRadius: 4,
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              📦 Наборы по N
            </span>
            . Дальше каждые N сканов автоматически закрывают новый набор.
          </li>
          <li>
            Если в каком-то наборе оказалось не N кодов — кнопка «Завершить
            сборку» отобьёт ошибкой. Доберите кодов до полного набора, либо
            подтвердите принудительное закрытие неполного (запишется в
            журнал).
          </li>
        </ol>
        <p className="muted">
          В Ozon-файле количество выводится в <b>наборах</b> (то есть КИЗ /
          размер набора), потому что на Ozon мы загружаем товарные наборы, а
          не маркировочные коды. В CSV для TechShare всё по-прежнему: все
          КИЗы построчно, как импортировались.
        </p>

        <h3 style={{ marginTop: 16 }}>🔄 Если в одном заказе наборы разных размеров</h3>
        <p>
          Иногда в одном заказе сначала идут наборы по 5 штук, потом по 4
          (например, перешли на другой комплект). Раньше система не давала
          менять размер внутри одной отгрузки; теперь можно — через{" "}
          <b>«Сменить размер набора»</b>:
        </p>
        <ol>
          <li>
            Закончите текущую партию до конца — последний набор должен быть
            полным (например, при размере 5 — отсканировать кратно 5).
          </li>
          <li>
            Под прогрессом «Режим наборов» появится кнопка{" "}
            <button
              disabled
              style={{
                background: "#fff",
                border: "1px solid #ad6800",
                color: "#ad6800",
                padding: "4px 10px",
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              🔄 Сменить размер набора
            </button>
            . Нажмите её — система откроет обучение по новой партии.
          </li>
          <li>
            Отсканируйте полностью первый набор новой партии (например, 4 шт)
            и подтвердите «Набор собран». Дальше всё как обычно — каждые 4
            скана закрывают набор.
          </li>
          <li>
            В Ozon-файл количество посчитается отдельно по каждой партии и
            сложится по артикулу. Например, артикул A: партия 1 = 2 набора
            × 5 шт, партия 2 = 3 набора × 4 шт → в xlsx: артикул A,
            количество 5 (2 + 3 набора).
          </li>
        </ol>
        <p className="help-warn">
          Кнопка <b>не появится</b>, если в текущей партии остался неполный
          набор (например, 13 КИЗ при размере 5). Доберите коды до 15 (или
          удалите лишние 3) и кнопка станет активной. Это защита от
          расхождения с TechShare.
        </p>
      </div>

      <div className="card">
        <h2>📷 Шаг 5. Сканирование</h2>
        <ol>
          <li>Наведите сканер на DataMatrix-код на марке, дождитесь звукового сигнала.</li>
          <li>
            В разделе «Коды в отгрузке» сразу появится новая строка — GTIN, serial и статус{" "}
            <span className="status status-selected">выбран</span>.
          </li>
          <li>Повторяйте для каждой единицы товара.</li>
          <li>
            Если что-то пошло не так — под формой появится красная плашка. Самые частые причины:
          </li>
          <li className="muted">
            <ul>
              <li>
                <b>code not found by composite_key</b> — такого кода нет ни в одной загруженной
                партии. Проверьте, что на склад пришла именно эта партия и что админ уже загрузил её в систему.
              </li>
              <li>
                <b>CodeAlreadyUsed</b> — этот код уже закрыт в другой отгрузке. Это брак
                процесса — спросите у админа.
              </li>
              <li>
                <b>CodeInOtherShipment</b> — код уже в другой незакрытой отгрузке. Сначала нужно
                убрать его оттуда.
              </li>
              <li>
                <b>DuplicateInShipment</b> — код уже есть в текущей отгрузке. Всё хорошо, просто
                этот скан повторный.
              </li>
              <li>
                <b>CodeWrittenOff</b> — код списан. Использовать нельзя.
              </li>
            </ul>
          </li>
        </ol>
        <p className="muted">
          Можно также ввести код руками в поле «composite_key» — пригодится, если сканер сломан
          или для пересчёта кода из брака.
        </p>
      </div>

      <div className="card">
        <h2>✅ Шаг 6. Завершить сборку (оператор / админ)</h2>
        <p>
          Когда все коды отгрузки отсканированы и их состав зафиксирован — нажмите на верхней
          карточке отгрузки синюю кнопку <b>«Завершить сборку»</b>. Отгрузка перейдёт из{" "}
          <span className="status status-scanning">Сканируется</span> в{" "}
          <span className="status status-picked">Собрана, нужен слот</span>, и с этого момента коды
          <b> нельзя </b> добавлять или удалять. В рабочий чат Bitrix24 мгновенно уйдёт
          уведомление{" "}
          <span className="mono">[TEKSHER] Сканирование завершено — нужен слот</span> —
          это сигнал менеджеру маркетплейса взять отгрузку в работу.
        </p>
        <p className="help-warn">
          Пустую отгрузку (без единого кода) завершить нельзя — система вернёт ошибку. Если
          смысл отгрузки отпал — просто оставьте её в <span className="status status-draft">Новая</span>,
          её можно использовать позже, либо переименовать и добавить коды.
        </p>
      </div>

      <div className="card">
        <h2>🟦 Шаг 6.5. Ozon-справочник и файл поставки (менеджер маркетплейса / админ)</h2>
        <p>
          Только для отгрузок, которые уедут на Ozon. Если работаете с
          Wildberries или Яндекс.Маркет — этот шаг пропустите, панель Ozon
          появится только когда (а) статус ≥{" "}
          <span className="status status-picked">Собрана, нужен слот</span> и (б) менеджер
          ещё не выбрал другой маркетплейс на шаге 7.
        </p>
        <ol>
          <li>
            Один раз для всего портала: зайдите в{" "}
            <NavHint>Ozon-справочник</NavHint> (ссылка в верхнем меню,
            видна только админу и менеджеру маркетплейса) и загрузите
            выгрузку из кабинета Ozon — файл <span className="mono">ozn_tov.csv</span>.
            При повторной заливке старые артикулы остаются, новые добавляются,
            совпадающие — обновляются. Справочник не теряется.
          </li>
          <li>
            На карточке отгрузки в статусе{" "}
            <span className="status status-picked">Собрана, нужен слот</span> появится блок{" "}
            <b>«Файл поставки для Ozon»</b>. Система автоматически матчит все
            коды отгрузки по <b>штрихкоду (GTIN)</b> со справочником и
            показывает таблицу: артикул / имя / количество.
          </li>
          <li>
            Если все товары найдены — нажмите{" "}
            <b>«Скачать readyfile.xlsx»</b>. Формат файла — ровно такой, как
            принимает ЛК Ozon для создания поставки: три колонки (артикул,
            имя, количество). Идите в ЛК Ozon, создайте поставку с этим
            файлом, получите номер поставки и слот.
          </li>
          <li>
            Если часть товаров отсутствует в справочнике — панель
            заблокирует скачивание и покажет список недостающих GTIN.
            Надо обновить справочник: в ЛК Ozon заведите новые товары,
            скачайте свежий <span className="mono">ozn_tov.csv</span>,
            залейте его через <NavHint>Ozon-справочник</NavHint>, вернитесь
            сюда — панель сама пересчитает и кнопка разблокируется.
          </li>
          <li>
            <b>Упаковочный лист.</b> В шапке любой отгрузки (в том числе
            старой и закрытой) есть кнопка <b>«📦 Упаковочный лист»</b> —
            скачивается xlsx в формате Ozon «Состав ГМ поставки» (ШК товара,
            артикул, количество, ШК ГМ, тип ГМ) — его можно загружать в ЛК
            Ozon. Короба восстанавливаются по времени печати стикера короба,
            поэтому печатайте стикер <b>в момент открытия</b> нового короба.
            Над иконками коробов написано <b>«Сканы → короб N»</b> — в этот
            короб записывается каждый новый скан. Печать стикера нового короба
            сама делает его текущим. Вернулись к уже открытому коробу — нажмите
            на его (красную) иконку и подтвердите «сделать текущим»; повторная
            печать — кликом по текущему (зелёному) коробу.
            Печать короба даёт <b>два стикера</b>: штрихкод короба (10 цифр:
            номер отгрузки + номер короба) и текстовый, как раньше, — оба
            клеятся на короб. По этому штрихкоду короб находится в листе
            «По коробам» упаковочного листа (что в нём лежит).
            ШК ГМ берутся из ZIP с этикетками Ozon, загруженного при
            бронировании; какую этикетку наклеили на какой короб/паллету,
            сверьте по вкладке «Сверка». Статус отгрузки кнопка не меняет.
          </li>
        </ol>
        <p className="muted">
          После того как Ozon дал номер поставки и слот — переходите к
          следующему шагу и заполняйте форму бронирования прямо на этой же
          карточке отгрузки.
        </p>
      </div>

      <div className="card">
        <h2>🚚 Шаг 7. Бронирование поставки (менеджер маркетплейса)</h2>
        <p>
          Этот шаг выполняет <b>менеджер маркетплейса</b> после того, как оператор нажал
          «Завершить сборку» и отгрузка перешла в статус{" "}
          <span className="status status-picked">Собрана, нужен слот</span>. До этого момента формы
          ниже не видно — менеджеру нечего делать, пока склад не закончил сборку.
        </p>
        <ol>
          <li>
            Дождитесь уведомления{" "}
            <span className="mono">[TEKSHER] Сканирование завершено — нужен слот</span> в
            чате Bitrix24 (или сами зайдите в <NavHint>Отгрузки</NavHint> и найдите
            отгрузку со статусом <span className="status status-picked">Собрана, нужен слот</span>).
          </li>
          <li>
            На стороне маркетплейса (кабинет Wildberries / Ozon / Яндекс.Маркет / …)
            забронируйте слот приёмки и получите <b>номер поставки</b> и{" "}
            <b>дату-время слота</b>.
          </li>
          <li>
            Откройте карточку отгрузки в TEKSHER — на ней появится блок{" "}
            <b>«Бронирование поставки»</b>. Заполните:
            <ul>
              <li>
                <b>маркетплейс</b> — WB / Ozon / Яндекс / другой;
              </li>
              <li>
                <b>номер поставки</b> — ровно тот номер, который выдал маркетплейс (например{" "}
                <span className="mono">WB-778899</span>);
              </li>
              <li>
                <b>слот (дата и время)</b> — момент приёмки на складе маркетплейса, по
                местному времени.
              </li>
            </ul>
          </li>
          <li>
            Нажмите <b>«Сохранить и отправить»</b>. Отгрузка перейдёт в статус{" "}
            <span className="status status-booked">Забронирована</span>, и в чат Bitrix24
            прилетит второе уведомление{" "}
            <span className="mono">[TEKSHER] Номер поставки добавлен — можно грузить CSV</span>
            — это сигнал оператору идти скачивать CSV и загружать в TechShare.
          </li>
        </ol>
        <p className="muted">
          Данные поставки можно править повторно (пока отгрузка в{" "}
          <span className="status status-booked">Забронирована</span>) — поправьте и нажмите
          «Обновить», в чат уйдёт новое уведомление.
        </p>
        <div className="help-warn">
          <b>Если блок «Бронирование поставки» не виден</b> — значит отгрузка ещё не
          доведена до <span className="status status-picked">Собрана, нужен слот</span> (оператор
          не нажал «Завершить сборку»). Попросите его это сделать — и форма сразу
          появится.
        </div>
      </div>

      <div className="card">
        <h2>💾 Шаг 8. Скачать CSV и загрузить в TechShare (оператор / админ)</h2>
        <p>
          Кнопка <b>«Скачать CSV»</b> становится активной только когда отгрузка
          в статусе <span className="status status-booked">Забронирована</span> (то есть
          менеджер маркетплейса уже ввёл номер поставки). До этого момента кнопка
          отключена — это намеренно, CSV должен соответствовать реальной отгрузке
          на маркетплейс.
        </p>
        <ol>
          <li>
            Нажмите <b>«Скачать CSV»</b>. Браузер сохранит файл{" "}
            <span className="mono">shipment-&lt;номер&gt;.csv</span>. Отгрузка
            перейдёт в статус <span className="status status-exported">CSV выгружен</span>.
          </li>
          <li>
            Этот файл — <b>точная побайтная подстрока исходного CSV TechShare</b>.
            Загрузите его как есть в личный кабинет TechShare и дождитесь
            подтверждения оборота.
          </li>
          <li>
            <b>Не редактируйте CSV</b> в Excel / блокноте / чём-либо ещё. Любое
            изменение сломает структуру, и TechShare откажется его принять.
          </li>
        </ol>
      </div>

      <div className="card">
        <h2>📥 Шаг 9. Ввести в оборот (оператор / админ)</h2>
        <p>
          После того как TechShare подтвердил приём CSV — вернитесь в карточку
          отгрузки и нажмите <b>«Ввести в оборот»</b>. Статус перейдёт в{" "}
          <span className="status status-in_turnover">Введена в оборот</span>.
          Коды пока остаются зарезервированными за отгрузкой, но уже обязаны
          уехать в маркетплейс.
        </p>
        <p>
          В рабочий чат Bitrix24 уйдёт уведомление{" "}
          <span className="mono">[TEKSHER] Ввод в оборот завершён — можно отгружать</span>.
          Пока система новая, к уведомлению добавляется напоминалка «не забудьте
          после физической отгрузки сменить статус на «Отгружена»»; после того
          как через систему прошло 10 отгрузок, напоминалка автоматически
          убирается.
        </p>
        <p className="help-warn">
          Если отгрузка зависнет в статусе{" "}
          <span className="status status-in_turnover">Введена в оборот</span>{" "}
          (или в более ранних:{" "}
          <span className="status status-picked">Собрана, нужен слот</span>,{" "}
          <span className="status status-booked">Забронирована</span>,{" "}
          <span className="status status-exported">CSV выгружен</span>) дольше
          72 часов — в рабочий чат Bitrix24 автоматически придёт сообщение с
          упоминанием управляющего склада. Повторяется не чаще раза в сутки.
        </p>
      </div>

      <div className="card">
        <h2>🔒 Шаг 10. Отгрузка (оператор / админ)</h2>
        <p>
          Когда отгрузка физически уехала в Ozon/WB/B24 — нажмите красную кнопку{" "}
          <b className="danger-text">«Отгружена»</b>. Все коды станут{" "}
          <span className="status status-used">использован</span> и больше никогда
          не попадут в другую отгрузку. Статус становится{" "}
          <span className="status status-closed">Отгружена</span>. Этот шаг
          необратим.
        </p>
        <p className="help-warn">
          Кнопка «Отгружена» доступна только из{" "}
          <span className="status status-in_turnover">Введена в оборот</span>:
          если CSV ещё не проведён через TechShare, сначала нажмите «Ввести в оборот».
        </p>
      </div>

      <div className="card">
        <h2>❌ Что НЕЛЬЗЯ делать</h2>
        <ul>
          <li>Редактировать скачанный CSV в Excel — сломается форматирование, TechShare откажет.</li>
          <li>Пытаться вручную «собрать» CSV из фоток или ТТН.</li>
          <li>Удалять или перезагружать одну и ту же партию дважды.</li>
          <li>Сканировать код и «класть» его в две разные отгрузки — система не даст, и это правильно.</li>
          <li>Использовать повторно использованный (used) код — он навсегда использован.</li>
          <li>
            Проставлять «номер поставки» для отгрузки, по которой ещё не нажата кнопка
            «Завершить сборку» — система не даст (409). Пока отгрузка в{" "}
            <span className="status status-scanning">Сканируется</span>, состав ещё может
            меняться, бронировать слот рано.
          </li>
          <li>
            Скачивать CSV и загружать его в TechShare <b>до</b> того, как менеджер
            маркетплейса ввёл номер поставки — кнопка «Скачать CSV» отключена именно
            для этого.
          </li>
          <li>
            Нажимать «Закрыть» <b>до</b> того, как TechShare принял CSV — закрытие
            необратимо, коды станут used навсегда.
          </li>
          <li>
            Пытаться удалить отгрузку в статусе{" "}
            <span className="status status-closed">Отгружена</span> —
            нельзя: коды уже физически уехали, затирать такую запись запрещено.
            Удаление возможно только для ещё не отгруженных отгрузок.
          </li>
        </ul>
      </div>

      <div className="card">
        <h2>🆘 Если что-то сломалось</h2>
        <ul>
          <li>
            Сохраните снимок экрана (Win+Shift+S / Cmd+Shift+4) со всей страницей и красной плашкой
            ошибки — с ним админ починит в разы быстрее.
          </li>
          <li>
            Перезагрузка страницы (F5) сбросит подключение сканера — это нормально, подключите
            заново.
          </li>
          <li>
            Если TechShare отказался принимать CSV, скажите админу — почти всегда это значит, что
            выгружен не тот файл или кого-то подменили (это баг системы, и его нужно найти).
          </li>
        </ul>
      </div>
    </div>
  );
}

function NavHint({ children }: { children: React.ReactNode }) {
  return <span className="nav-hint">{children}</span>;
}

function ScannerBadge({ kind }: { kind: "disconnected" | "connecting" | "connected" }) {
  const label =
    kind === "connected"
      ? "сканер подключён"
      : kind === "connecting"
        ? "подключение…"
        : "сканер не подключён";
  return (
    <span className={`scanner-indicator scanner-${kind}`}>
      <span className="scanner-dot" />
      {label}
    </span>
  );
}
